import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import values from 'lodash/values';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import pick from 'lodash/pick';

import { SubmissionError } from 'redux-form';

import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import { createSelectOptionsFromSysEnums, createSelectValuesUseIntersection } from '../shared/form-elements/select';
import checkPermissions from '../shared/check-permissions';

// actions types
const FETCH_DATA_SUCCESS = 'campaign-domains/fetch-data-success';
const FETCH_DATA_ERROR = 'campaign-domains/fetch-data-error';

const SUBMIT_SUCCESS = 'campaign-domains-domains/submit-success';
const SUBMIT_ERROR = 'campaign-domains/submit-error';

const REMOVE_SUCCESS = 'campaign-domains/remove-success';
const REMOVE_ERROR = 'campaign-domains/remove-error';

const SORT_BY_COLUMN = 'campaign-domains/table-sort-by-column';
const SHOW_MORE_ROWS = 'campaign-domains/table-show-more-rows';

const TOGGLE_PROGRESS_BAR = 'campaign-domains/toggle-progress-bar';

const FETCH_SUGGEST_SUCCESS = 'campaign-domains/fetch-suggest-success';
const FETCH_SUGGEST_ERROR = 'campaign-domains/fetch-suggest-error';

// permissions
export const allowedPermissions = {
	'view': 'campaign_domains_list',
	'create': 'campaign_domains_create',
	'edit': 'campaign_domains_create',
	'delete': 'campaign_domains_delete'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	table: {
		columns: {
			id: {
				index: 'id',
				title: 'Id',
				direction: 'desc'
			},
			domain: {
				index: 'domain',
				title: 'Domain',
				direction: 'desc'
			},
			adv_system: {
				index: 'adv_system',
				title: 'Advertising system',
				direction: 'desc'
			},
			affiliates: {
				index: 'affiliates',
				title: 'Affiliates',
				direction: 'desc'
			},
			count: {
				index: 'count',
				title: 'Campaigns',
				direction: 'desc',
				className: 'count-header'
			},
			created_at: {
				index: 'created_at',
				title: 'Created',
				direction: 'desc'
			},
			updated_at: {
				index: 'updated_at',
				title: 'Updated',
				direction: 'desc'
			},
			actions: {
				title: 'Actions'
			}
		},
		sortByColumn: {
			index: 'id',
			title: 'Id',
			direction: 'desc'
		},
		rowsNumber: 1000
	},
	form: {
		domain: '',
		adv_system: null,
		affiliate_ids: null,
		has_own_https: false
	},
	error: false
};

// actions
export const actions = {
	fetchData: () => dispatch => {
		(http({
			url: `${endpoints.CAMPAIGN_DOMAINS_LIST}`,
			method: 'get',
			actions: [
				FETCH_DATA_SUCCESS,
				FETCH_DATA_ERROR
			]
		}))(dispatch);
	},

	submitForm: (formData, domainId = null) => (dispatch, getState) => {
		const request = http({
			url: !domainId ? endpoints.CAMPAIGN_DOMAINS_LIST : `${endpoints.CAMPAIGN_DOMAINS_LIST}/${domainId}`,
			method: !domainId ? 'post' : 'put',
			actions: [
				SUBMIT_SUCCESS,
				SUBMIT_ERROR
			],
			data: pick({
				domain: formData.domain || '',
				adv_system: formData.adv_system && formData.adv_system[0].value || '',
				affiliate_ids: formData.affiliate_ids && formData.affiliate_ids.length &&
					formData.affiliate_ids.map(affiliate => affiliate.value) || null,
				has_own_https: formData.has_own_https
			}, Object.keys(getState().campaignDomains.form))
		});

		return request(dispatch)
			.then(action => {
				if (action.type === SUBMIT_SUCCESS) {
					dispatch(actions.redirectToModuleIndex());
				}

				if (action.type === SUBMIT_ERROR && action.payload.response.status === 400) {
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
	},

	remove: id => dispatch => {
		const request = http({
			url: `${endpoints.CAMPAIGN_DOMAINS_LIST}/${id}`,
			method: 'delete',
			actions: [
				REMOVE_SUCCESS,
				REMOVE_ERROR
			]
		});

		dispatch(actions.toggleProgressBar());

		request(dispatch);
	},

	sortByColumn: columnIndex => ({
		type: SORT_BY_COLUMN,
		payload: columnIndex
	}),

	showMoreRows: () => ({
		type: SHOW_MORE_ROWS
	}),

	redirectToModuleIndex: () => dispatch => {
		// @todo Adhock for redux-form submit functionality.
		setTimeout(() => dispatch(replace('campaign-domains/')), 100);
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	}),

	loadAffiliatesLookup: text => dispatch => {
		const request = http({
			url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
			method: 'get',
			actions: [
				FETCH_SUGGEST_SUCCESS,
				FETCH_SUGGEST_ERROR
			]
		});

		return request(dispatch).then(action => {
			if (action.type === FETCH_SUGGEST_SUCCESS) {
				return {
					options: createSelectOptionsFromSysEnums(action.payload.data)
				};
			}
		});
	}
};

// reducers
export default handleActions({
	[LOCATION_CHANGE]: (state, action) => {
		if (
			action.payload.pathname.indexOf('campaign-domains') !== -1 &&
			action.payload.pathname.indexOf('campaign-domains/create') === -1
		) {
			return {
				...state,
				data: null
			};
		}
		return state;
	},
	[FETCH_DATA_SUCCESS]: (state, action) => ({
		...state, data: action.payload.data, showProgressBar: false, error: false
	}),
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[SORT_BY_COLUMN]: (state, action) => {
		const { sortByColumn, columns } = state.table;
		const columnIndex = action.payload;
		const direction = sortByColumn['index'] == columnIndex ?
			sortByColumn.direction === 'desc' ? 'asc' : 'desc' :
			columns[columnIndex].direction;
		return {
			...state,
			table: {
				...state.table,
				sortByColumn: {
					...columns[columnIndex],
					direction
				}
			}
		};
	},
	[SHOW_MORE_ROWS]: state => ({
		...state,
		table: {
			...state.table,
			rowsNumber: state.table.rowsNumber += initialState.table.rowsNumber
		}
	}),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
	[REMOVE_SUCCESS]: state => ({ ...state, data: null }),
	[REMOVE_ERROR]: state => ({ ...state, error: true }),
	[SUBMIT_ERROR]: (state, action) => {
		const { response: { status = null, data = null } } = action.payload;
		if (status === 400 && !!data) return state;
		return { ...state, error: true };
	}
}, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.campaignDomains.data,
	getTable: state => {
		const { table, data } = state.campaignDomains;
		const rows = state.campaignDomains.data ? sortColumns(data, table.sortByColumn) : [];
		let partialData = cloneDeep(rows.slice(0, table.rowsNumber));
		partialData = partialData.map(row => {
			row.adv_system = find(state.root.lists['adv-systems'], { id: row.adv_system }).text;
			return row;
		});
		return {
			headers: values(table.columns),
			rows: partialData,
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getAdvSystemSelectOptions: state => createSelectOptionsFromSysEnums(state.root.lists['adv-systems']),
	getIntl: state => intl(state),
	getProgressBar: state => state.campaignDomains.showProgressBar,
	getCampaignDomainFieldsById: state => domainId => {
		const current = find(state.campaignDomains.data, { id: parseInt(domainId) });

		return {
			...state.campaignDomains.form,
			...current,
			adv_system: createSelectValuesUseIntersection(
				selectors.getAdvSystemSelectOptions(state),
				[current.adv_system]
			),
			affiliate_ids: current.affiliates ? createSelectOptionsFromSysEnums(current.affiliates) : null
		};
	},
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.campaignDomains.error
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(rows, row => {
		let value;
		sortedColumn.index === 'count' ? value = parseInt(row[sortedColumn.index]) : value = row[sortedColumn.index];

		switch (sortedColumn.index) {
			case 'id':
			case 'domain':
			case 'adv_system':
			case 'affiliate':
			case 'created_at':
			case 'updated_at':
			case 'count':
				return value;
			default:
				return value;
		}
	}, sortedColumn.direction);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/campaign-domains/index.js